import { IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { CockpitPanelIntlKeysEnum } from '@components/cockpit/cockpit.types';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import Panel from '@components/cockpit/panels/Panel';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { PlatformSelectors } from '@redux/config/config.selectors';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Platform } from '@/types/config/config.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import DeletePlatformModal from './DeletePlatformModal';
import PlatformFormContainer from './PlatformFormContainer';

interface PlatformsActionsProps {
  canCreatePlatform: boolean;
  platformsLength: number;
  onCreate: () => void;
}

function PlatformsActions({ canCreatePlatform, platformsLength, onCreate }: Readonly<PlatformsActionsProps>) {
  return (
    <>
      <Text color="neutral.500" textTransform="lowercase" userSelect="none">
        <FormattedMessage id="components.platforms.result" values={{ count: platformsLength }} />
      </Text>
      {canCreatePlatform && (
        <IconButton aria-label="Add platform" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function PlatformsHeader() {
  return (
    <Text width="240px" fontWeight="medium" color="neutral.500">
      <FormattedMessage id="components.platforms.platform" />
    </Text>
  );
}

interface PlatformsProps {
  setEditedPlatformCode: Dispatch<SetStateAction<string | null | undefined>>;
}

function Platforms({ setEditedPlatformCode }: Readonly<PlatformsProps>) {
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const canCreatePlatform = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_PLATFORM));
  const canEditPlatform = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_PLATFORM));
  const canDeletePlatform = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_PLATFORM));

  return (
    <CockpitListLayout
      dialog={<DeletePlatformModal selectedPlatform={selectedPlatform} setSelectedPlatform={setSelectedPlatform} />}
      actions={
        <PlatformsActions
          canCreatePlatform={canCreatePlatform}
          platformsLength={platforms.length}
          onCreate={() => setEditedPlatformCode(undefined)}
        />
      }
      header={platforms.length > 0 && <PlatformsHeader />}
    >
      <For
        each={platforms}
        fallback={
          canCreatePlatform ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedPlatformCode(undefined)}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.platforms.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(platform, index) => {
          return (
            <CockpitRow index={index} listLength={platforms.length} key={index}>
              <Text
                width="220px"
                fontWeight="medium"
                onClick={() => navigator.clipboard.writeText(platform.code)}
                cursor="pointer"
              >
                {platform.name}
              </Text>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditPlatform && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => setEditedPlatformCode(platform.code)}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canDeletePlatform && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedPlatform(platform)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}

export function PlatformManagement() {
  const { formatMessage } = useIntl();
  const [editedPlatformCode, setEditedPlatformCode] = useState<string | null | undefined>(null);

  return (
    <Panel
      header={
        <CockpitPanelHeader label={formatMessage({ id: `components.sidebar.${CockpitPanelIntlKeysEnum.PLATFORM}` })} />
      }
    >
      {editedPlatformCode === null ? (
        <Platforms setEditedPlatformCode={setEditedPlatformCode} />
      ) : (
        <PlatformFormContainer editedPlatformCode={editedPlatformCode} setEditedPlatformCode={setEditedPlatformCode} />
      )}
    </Panel>
  );
}
